import { CellContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import Transaction from '@/models/Transaction';
import DualCalendarDate from '@/components/ui/DualCalendarDate/DualCalendarDate';
import useCalendarMode from '@/hooks/useCalendarMode';

export interface TransactionNameMeta {
  showKindPrefix: boolean;
  isSidebarDocked: boolean;
  onOpen: (transaction: Transaction) => void;
}

// A stable cell component keeps keyboard focus when preferences or balances refresh.
export const TransactionNameCell = ({ row, column }: CellContext<Transaction, unknown>) => {
  const { t } = useTranslation();
  const { showKindPrefix, isSidebarDocked, onOpen } = column.columnDef.meta as TransactionNameMeta;
  const name = row.original.contactName || '';
  const kind = row.original.kind || '';
  const method = row.original.paymentMethod;
  const displayName = !isSidebarDocked && kind ? `${name} — ${kind}` : name;
  const title = displayName || name;
  // HAT-1309: a bank standing-order row stands for many orders and opens their list; unlike every
  // other row, its name is not a person, so it says what the click does (Druk's approved mockup).
  const opensList = row.original.future?.source === 'standingOrder';

  return (
    <button
      type="button"
      className={`w-full min-w-0 ${isSidebarDocked ? 'text-start' : 'text-center'}`}
      title={title}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(row.original);
      }}
    >
      {showKindPrefix && <span className={isSidebarDocked ? 'block' : 'sr-only'}>{kind} </span>}
      <span className={`name ${isSidebarDocked ? 'block truncate font-semibold' : 'block font-semibold'}`} data-transaction-name>
        {displayName}
      </span>
      {opensList && isSidebarDocked && (
        <span className="block body-sm-link text-(--font-link)" data-transaction-open-list>
          {t('futureBankStandingOrdersShowList')}
        </span>
      )}
      {!isSidebarDocked && method && (
        <span data-transaction-method className="block">
          {t(method)}
        </span>
      )}
    </button>
  );
};

// Variant B dual date rail (HAT-1210). Hierarchy follows the לועזי|עברי toggle.
// Missing movements still get a placeholder so the column never collapses (HAT-1201 / HAT-1205).
export const TransactionDateCell = ({ getValue, row }: CellContext<Transaction, unknown>) => {
  const { t } = useTranslation();
  const [calendarMode] = useCalendarMode();
  // Pass the raw wire value so DualCalendarDate can normalize suffixless ISO as UTC.
  return <DualCalendarDate value={getValue() ?? row.original.date} mode={calendarMode} missingLabel={t('missingTransactionDate')} />;
};
